import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete, { AutocompleteChangeDetails, AutocompleteChangeReason } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, IconButton } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import debounce from 'lodash/debounce';
import { styled } from '@mui/material/styles';
import { PlusCircleOutline } from 'mdi-material-ui';
import VigoEditDrawer from 'src/@vigo/vigo-masterdataeditor';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { useVigoToast } from 'src/@core/hooks/useVigoToast';
import React from 'react';
import { useDidMountEffect } from 'src/@core/hooks/useDidMountEffect';
import {
  fetchAutocompleteRows,
  fetchFirst25RowsFromSource,
  getDisplayValue,
  getMasterDataTableId,
  loadMoreRows,
  setInitialDropdownValue
} from './helpers';
import { IAutoCompleteInput } from './types';

const InputContainer = styled('div')({
  display: 'flex',
  alignItems: 'flex-start'
});

const VigoAutoComplete = React.forwardRef(
  (
    {
      name,
      label,
      disablePortal,
      sx,
      fullWidth,
      displayName,
      serviceEndpoint,
      serviceLocation,
      datasourceFilter,
      helperText,
      error,
      errormessage,
      queryColumns,
      displayColumns,
      displayColumnLabels,
      mapColumnValue,
      onChange,
      onFocus,
      value,
      required,
      allowAddNewOption,
      disabled,
      readOnly,
      evaluateFilter,
      clearDepenancies,
      dependancyProperty,
      useDataGridContext,
      dataGridContextParams,
      useQueryTextAsFilter,
      autoCompleteOptionObject,
      autoHighlight = true,
      autoSelect = false,
      masterDataObject,
      customSort,
      freeSolo = false,
      displayColumnHeaderStyles = [],
      displayColumnOptionsStyles = [],
      tenantId,
      partnerId,
      onClear,
      customOptions = [],
      defaultToCustomOption,
      ignoreFetch,
      overrideIsInitialLoad,
      variant = 'outlined',
      customMenuOptionSx
    }: IAutoCompleteInput,
    ref
  ) => {
    // State
    const [autocompleteOptions, setAutocompleteOptions] = useState<Array<any>>([]);
    const [autocompleteValue, setAutocompleteValue] = useState<string | null>(null);
    const [initialValue, setInitialValue] = useState<string | null>(null);
    const [initialValueObject, setInitialValueObject] = useState<any | null>(null);
    const [optionObjects, setOptionObjects] = useState<any[]>([]);
    const [inputValue, setInputValue] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [fetching, setFetching] = useState<boolean>(false);
    const [masterDataTableId, setMasterDataTableId] = useState<string>('');
    const [canLoadMore, setCanLoadMore] = useState<boolean>(false);
    const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
    const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

    const PAGE_SIZE = 50;
    const sort = customSort ? customSort : serviceLocation == 'systemconfig' ? 'sequence' : 'displayorder';

    // Hooks
    const { t } = useVigoTranslation();
    const { vigoToast } = useVigoToast({});
    const listRef = useRef<any>(null);
    const savedScrollPosition = useRef<number>(0);
    const isPaginationLoading = useRef<boolean>(false);
    const isScrollRestoring = useRef<boolean>(false);

    let dataGridApiRef: any;
    if (useDataGridContext) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      dataGridApiRef = useGridApiContext();
    }

    // Filter customOptions by inputValue
    const filteredCustomOptions = useMemo(() => {
      if (!inputValue) return customOptions;

      return customOptions.filter(option =>
        (option.description || option.name || '').toLowerCase().includes(inputValue.toLowerCase())
      );
    }, [customOptions, inputValue]);

    // Create combined options with filtered custom options at the top
    const combinedOptions = useMemo(() => {
      const customOptionIds = filteredCustomOptions.map(option => option.id);
      const regularOptions = autocompleteOptions.filter(id => !customOptionIds.includes(id));

      return [...customOptionIds, ...regularOptions];
    }, [filteredCustomOptions, autocompleteOptions]);

    // Create combined option objects with filtered custom options included
    const combinedOptionObjects = useMemo(() => {
      return [...filteredCustomOptions, ...optionObjects];
    }, [filteredCustomOptions, optionObjects]);

    const shouldAutoSelect = useMemo(() => {
      return !!autoSelect && combinedOptions.length === 1;
    }, [autoSelect, combinedOptions.length]);

    const handleAddOptionClicked = () => setDrawerOpen(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getOptionsDelayed = useCallback(
      debounce(text => {
        setAutocompleteOptions([]);
        fetchAutocompleteRows(
          text,
          serviceEndpoint,
          serviceLocation,
          setAutocompleteOptions,
          setLoading,
          useQueryTextAsFilter,
          queryColumns,
          displayName,
          inputValue,
          datasourceFilter,
          evaluateFilter,
          sort,
          tenantId,
          partnerId,
          setCanLoadMore,
          setCurrentPageNumber,
          initialValue,
          initialValueObject,
          setOptionObjects,
          vigoToast,
          PAGE_SIZE
        );
      }, 500),
      []
    );

    function handleInputChange(event: any, value: any, reason: string) {
      if (reason === 'clear' && onClear && typeof onClear === 'function') {
        onClear(event);
      }

      setCanLoadMore(false);
      setCurrentPageNumber(0);

      if (event != undefined) {
        setInputValue(event.target.value);
      }

      if (event == null || event.target.value == undefined) {
        setInputValue('');
      }
    }

    // How the option is rendered in the dropdown if DisplayColumns are provided
    const createTableBody = useMemo(
      () => (props: any, option: any) => {
        return (
          <span {...props}>
            {displayColumns!.map((opt: any, index) => {
              const rowData = combinedOptionObjects.find(x => x.id === option);
              const rawValue = rowData?.[opt];
              const displayValue = mapColumnValue ? mapColumnValue(opt, rawValue, rowData) : rawValue;

              return (
                <Box
                  component="span"
                  key={opt?.id ?? Math.random() * 999999}
                  sx={{
                    width: `${100 / displayColumns!.length}%`,
                    textAlign: 'left',
                    ...(displayColumnOptionsStyles?.[index] ?? {})
                  }}>
                  {displayValue}
                </Box>
              );
            })}
          </span>
        );
      },
      [displayColumnOptionsStyles, displayColumns, combinedOptionObjects, mapColumnValue]
    );

    // How the top of the dropdown is rendered if DisplayColumns are provided
    const CustomDropdown = useMemo(
      () =>
        React.forwardRef((props: any, ref) => {
          listRef.current = ref;
          if (combinedOptions.length > 0) {
            const headers = displayColumnLabels || displayColumns!;

            return (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {headers.map((header, index) => (
                        <TableCell
                          key={header}
                          sx={{
                            width: `${100 / displayColumns!.length}%`,
                            ...(displayColumnHeaderStyles?.[index] ?? {})
                          }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                </Table>
                <ul {...props} ref={ref} />
              </TableContainer>
            );
          } else {
            return <ul {...props} ref={ref} />;
          }
        }),
      [combinedOptions.length, displayColumnHeaderStyles, displayColumns, displayColumnLabels]
    );

    const addedNewValue = (data: any) => {
      if (data.id !== undefined) {
        optionObjects.push(data);
        setOptionObjects(optionObjects);

        autocompleteOptions.push(data.id);
        setAutocompleteValue(String(data.id));
      }

      setDrawerOpen(false);
    };

    const handleChange = useCallback(
      (
        event: SyntheticEvent<Element, Event>,
        value: any,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<any> | undefined
      ) => {
        if (useDataGridContext && dataGridContextParams) {
          const field = dataGridContextParams.field;
          const id = dataGridContextParams.id;

          dataGridApiRef.current.setEditCellValue({
            id,
            field,
            value: value
          });
        }

        if (autoCompleteOptionObject) {
          const object = combinedOptionObjects.find(x => x.id == value);

          autoCompleteOptionObject(object);
        }

        onChange?.(event, value, reason, details);
        setAutocompleteValue(value);

        if (initialValue && value == '') {
          setInitialValue('');
          setInitialValueObject(null);
        }

        if (clearDepenancies && dependancyProperty) {
          clearDepenancies(dependancyProperty);
        }
      },
      [
        useDataGridContext,
        dataGridContextParams,
        autoCompleteOptionObject,
        combinedOptionObjects,
        onChange,
        initialValue,
        clearDepenancies,
        dependancyProperty,
        dataGridApiRef
      ]
    );

    const handleScroll = (event: React.UIEvent<HTMLUListElement, UIEvent>) => {
      const node: any = event.target;

      const currentScrollPosition = node.scrollTop + node.clientHeight;

      // Check if user has scrolled to bottom and more data is available
      if (node.scrollHeight - currentScrollPosition <= 1 && canLoadMore && !loading) {
        // Save current scroll position before loading more items
        savedScrollPosition.current = node.scrollTop;
        isPaginationLoading.current = true;
        isScrollRestoring.current = true;

        loadMoreRows(
          setCurrentPageNumber,
          currentPageNumber,
          setCanLoadMore,
          serviceLocation,
          serviceEndpoint,
          useQueryTextAsFilter,
          queryColumns,
          displayName,
          inputValue,
          datasourceFilter,
          evaluateFilter,
          sort,
          tenantId,
          partnerId,
          optionObjects,
          autocompleteOptions,
          initialValue,
          setOptionObjects,
          setAutocompleteOptions,
          vigoToast,
          PAGE_SIZE,
          setLoading
        ).catch(() => {
          isScrollRestoring.current = false;
          isPaginationLoading.current = false;
          savedScrollPosition.current = 0;
        });
      }
    };

    const toggleEditDrawer = () => {
      if (!drawerOpen) {
        setDrawerOpen(true);
      } else {
        setDrawerOpen(false);
      }
    };

    useEffect(() => {
      if (!autoSelect) {
        return;
      }

      if (inputValue.trim() !== '') {
        return;
      }

      if (combinedOptions.length !== 1) {
        return;
      }

      const onlyOption = combinedOptions[0];

      if (!onlyOption || autocompleteValue === onlyOption) {
        return;
      }

      handleChange({} as SyntheticEvent<Element, Event>, onlyOption, 'selectOption');
    }, [autoSelect, inputValue, combinedOptions, autocompleteValue, handleChange]);

    useEffect(() => {
      if (!value) {
        // Check if we should default to a custom option when no value is provided
        if (
          defaultToCustomOption &&
          isInitialLoad &&
          customOptions.some(option => option.id === defaultToCustomOption)
        ) {
          setAutocompleteValue(defaultToCustomOption);

          return;
        }

        setAutocompleteValue(null);

        return;
      }

      if (serviceEndpoint == undefined || serviceLocation == undefined) {
        setAutocompleteOptions([]);

        return;
      }

      if (customOptions.some(option => option.id === value)) {
        setAutocompleteValue(value);

        return;
      }

      if (defaultToCustomOption && ignoreFetch && isInitialLoad) {
        const matchingCustomOption = customOptions.find(option => option.id === defaultToCustomOption);
        if (matchingCustomOption) {
          setAutocompleteValue(defaultToCustomOption);
          setIsInitialLoad(false);

          return;
        }
      }

      if (value && value != '' && (isInitialLoad || overrideIsInitialLoad) && !ignoreFetch) {
        const existingObject = optionObjects.find((x: any) => x.id == value);
        if (existingObject) {
          // Value was just selected from the dropdown — data is already loaded.
          // Sync initialValue/initialValueObject so future fetches preserve it.
          setAutocompleteValue(value);
          setInitialValue(value);
          setInitialValueObject(existingObject);
        } else {
          setFetching(true);
          setInitialDropdownValue(
            value,
            serviceEndpoint,
            serviceLocation,
            sort,
            tenantId,
            partnerId,
            setOptionObjects,
            setAutocompleteValue,
            setInitialValue,
            setInitialValueObject,
            vigoToast
          );
          setFetching(false);
        }
      }
      setIsInitialLoad(false);
      getMasterDataTableId(allowAddNewOption, serviceEndpoint, serviceLocation, setMasterDataTableId, vigoToast);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, defaultToCustomOption]);

    useDidMountEffect(() => {
      if (serviceEndpoint == '' || serviceLocation == '') {
        return;
      }

      if (!open) {
        return;
      }

      setLoading(true);
      getOptionsDelayed(inputValue);

      return;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);

    useEffect(() => {
      /**
       * Handle getting a ref to the list box when displayColumns are provided.
       * This is needed to update the scroll position of the list box.
       */
      if (displayColumns == undefined) {
        return;
      }
      const list = document.querySelector('ul.MuiAutocomplete-listbox');
      listRef.current = list;
    });

    // Reset scroll restoration state after new options finish loading
    useEffect(() => {
      if (!isPaginationLoading.current) {
        return;
      }

      const resetTimer = setTimeout(() => {
        isPaginationLoading.current = false;
        isScrollRestoring.current = false;
        savedScrollPosition.current = 0;
      }, 500);

      return () => clearTimeout(resetTimer);
    }, [autocompleteOptions]);

    return (
      <>
        {fetching && null}
        {!fetching && (
          <>
            <Autocomplete
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={customMenuOptionSx}>
                    {getDisplayValue(option, combinedOptionObjects, displayName, displayColumns) || ''}
                  </Box>
                </li>
              )}
              freeSolo={freeSolo}
              autoSelect={shouldAutoSelect}
              options={combinedOptions}
              filterOptions={x => x}
              onChange={handleChange}
              onInputChange={handleInputChange}
              ListboxProps={
                {
                  onScroll: handleScroll,
                  ref: (node: HTMLElement | null) => {
                    if (node) {
                      listRef.current = node;
                      // Immediately restore scroll position when listbox is recreated
                      if (isPaginationLoading.current && savedScrollPosition.current > 0) {
                        node.scrollTop = savedScrollPosition.current;
                      }
                    }
                  }
                } as any
              }
              onOpen={() => {
                if (evaluateFilter) {
                  datasourceFilter = evaluateFilter(datasourceFilter ? datasourceFilter : '');
                }
                setOpen(true);
                if (inputValue == '' && autocompleteOptions.length == 0) {
                  fetchFirst25RowsFromSource(
                    serviceEndpoint,
                    serviceLocation,
                    setLoading,
                    useQueryTextAsFilter,
                    queryColumns,
                    displayName,
                    inputValue,
                    datasourceFilter,
                    evaluateFilter,
                    sort,
                    tenantId,
                    partnerId,
                    setCanLoadMore,
                    setCurrentPageNumber,
                    initialValue,
                    initialValueObject,
                    setOptionObjects,
                    setAutocompleteOptions,
                    vigoToast,
                    PAGE_SIZE
                  );
                }
              }}
              onClose={() => {
                setOpen(false);
                setAutocompleteOptions([]);
                setInputValue('');
              }}
              {...(displayColumns != undefined && {
                ListboxComponent: CustomDropdown,
                renderOption: createTableBody
              })}
              getOptionLabel={option =>
                getDisplayValue(option, combinedOptionObjects, displayName, displayColumns, mapColumnValue) || ''
              }
              isOptionEqualToValue={(option, value) => {
                return option === value;
              }}
              size="small"
              value={autocompleteValue}
              renderInput={params => (
                <InputContainer>
                  <TextField
                    {...params}
                    autoComplete="off"
                    variant={variant}
                    label={label == undefined ? '' : t(label)}
                    inputRef={ref}
                    helperText={error ? t(errormessage) : t(helperText)}
                    InputProps={{
                      ...params.InputProps,
                      sx: customMenuOptionSx,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                    {...{ fullWidth, error, required, name }}
                  />
                  {allowAddNewOption && (
                    <IconButton onClick={handleAddOptionClicked}>
                      <PlusCircleOutline />
                    </IconButton>
                  )}
                </InputContainer>
              )}
              {...{ disabled, readOnly, fullWidth, loading, sx, onFocus, open, disablePortal, autoHighlight }}
            />
            <VigoEditDrawer
              open={drawerOpen}
              toggle={toggleEditDrawer}
              refresh={addedNewValue}
              title={'Add ' + serviceEndpoint}
              endPoint={serviceEndpoint}
              selectedTableId={masterDataTableId}
              rowId={''}
              {...{ masterDataObject, serviceLocation }}
            />
          </>
        )}
      </>
    );
  }
);

export default VigoAutoComplete;
